import * as React from 'react';
import './editMode.scss';
import '../Tabular/tabular.scss';
import { Cancel, Save } from 'rcd-cpp-ui/ui/button';
import TextareaAutosize from '@mui/material/TextareaAutosize/TextareaAutosize';
import { ColField } from '../../../types/ColField';
import { Metadata, Schema, SchemaField } from '../../../types/Metadata';
import { DataType } from '../../../enums/DataType';
import { DatePicker } from '../../DatePicker/datePicker';
import { NodeType } from '../../../enums/NodeType';
import { ReferenceSearch } from '../../ReferenceSearch/referenceSearch';
import { useSelector } from '../../../utils/useSelector';
import {
  getDefaultDataAPI,
  getUseCaseSearchAPI
} from '../../MetadataForm/ReferenceField/referenceField';
import { FieldType } from '../../../enums/FieldType';
import { TreeFieldUtils } from '../../../utils/treeFieldUtils';
import { fieldIsRequired, getSchema } from '../../../utils';
import { Field } from '../../MetadataForm/metadataFormField';
import {
  updateEditValue,
  updateSelectedCellPosition,
  areObjectsEqual
} from '../../../utils/editModeUtils';
import { formatDate } from '../../../utils';
import { EditBoxAdjustment } from '../../../enums/EditBoxAdjustment';
import { KeyName } from '../../../enums/KeyName';
import { FieldData } from '../../../types/Field';
import { SelectedCell } from '../../../types/SelectedCell';

type EMDProps = {
  selectedCellPosition: any;
  selectedCell: SelectedCell;
  setSelectedCell: any;
  editValue: any;
  setEditValue: any;
  item: Metadata;
  field: ColField;
  minEditBoxWidth: number;
  navFlyoutWidth: number;
  originalValue: any;
  setOriginalValue: any;
  setIsTreeSelect: any;
  editValueTree: Partial<Metadata>;
  originalValueTree: Partial<Metadata>;
  updater: any;
  parentTreeField: SchemaField;
  setParentTreeField: any;
  treeFieldGroupings: any;
  schemasProp: Schema[];
  updateData: any;
  updateFields: any;
  updateField: any;
  data: any;
  invalidCells: any;
  addInvalidCell: any;
  removeInvalidCell: any;
  updatesInvalidCells: any;
  toasters: any;
  fields: any;
  document: Document;
  setSelectedCellPosition: (position: any) => void;
  totalColumnCount?: number;
};

type BoxAdjustments = {
  editBottomAdjustment: number;
  editRightAdjustment: number;
};

export const generateBoxAdjustments = (
  selectedCellPosition,
  minEditBoxWidth,
  schemaField,
  isTree,
  totalColumnCount
) => {
  let bottomCheckValue = EditBoxAdjustment.DefaultBottomCheck;
  // Some of the field types display differently, so manual adjustments
  // are needed for bottom of the screen edge case
  if (schemaField?.dataType == DataType?.boolean) {
    bottomCheckValue = EditBoxAdjustment.BooleanBottomCheck;
    minEditBoxWidth = minEditBoxWidth / EditBoxAdjustment.BooleanWidthDivisor;
  } else if (schemaField?.fieldType == FieldType.reference) {
    if (!!isTree) {
      bottomCheckValue = EditBoxAdjustment.ReferenceBottomCheckTree;
    } else {
      bottomCheckValue = EditBoxAdjustment.ReferenceBottomCheck;
    }
  }

  const isSmallColumnTable = totalColumnCount <= 3;

  const editBottomAdjustment =
    selectedCellPosition?.bottom + bottomCheckValue > window?.innerHeight
      ? Math.abs(
          window?.innerHeight - selectedCellPosition?.bottom - bottomCheckValue
        )
      : 0;

  let editRightAdjustment = 0;

  if (isSmallColumnTable) {
    editRightAdjustment = 0;
  } else if (
    selectedCellPosition?.left + minEditBoxWidth >
    window?.innerWidth
  ) {
    editRightAdjustment = Math.abs(
      window?.innerWidth -
        selectedCellPosition?.left -
        minEditBoxWidth -
        EditBoxAdjustment.EditBoxRightPadding
    );
  }

  return { editBottomAdjustment, editRightAdjustment };
};

export const isCellValid = (
  item: any,
  field: any,
  document: Document
): boolean => {
  if (!item?.props?.refId || !field?.field) {
    return false;
  }
  const cellId = `${item.props.refId}-${field.field}`;
  const cellElement = document?.getElementById(cellId);
  return !!cellElement;
};

export const EditModeDropdown: React.FC<EMDProps> = ({
  selectedCellPosition,
  selectedCell,
  setSelectedCell,
  editValue,
  setEditValue,
  field,
  minEditBoxWidth,
  navFlyoutWidth,
  originalValue,
  setOriginalValue,
  setIsTreeSelect,
  editValueTree,
  originalValueTree,
  updater,
  parentTreeField,
  setParentTreeField,
  treeFieldGroupings,
  schemasProp,
  updateData,
  updateFields,
  updateField,
  data,
  removeInvalidCell,
  toasters,
  fields,
  document,
  setSelectedCellPosition,
  totalColumnCount = 0
}) => {
  const editRef = React.useRef(null);
  const textareaRef = React.useRef(null);
  const [boxAdjustments, setBoxAdjustments] =
    React.useState<BoxAdjustments>(null);
  const [dataType, setDataType] = React.useState<DataType | NodeType>(null);
  const [fieldType, setFieldType] = React.useState<FieldType>(null);
  const [refreshSearchTrigger, setRefreshSearchTrigger] = React.useState(true);
  const [cache] = React.useState({});
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const getAPI = useSelector(s => s.app.elFn.getAPI);
  const workspaceSettings = useSelector(
    s => s.planning.selectedWorkspace.setting
  );
  const refSearchSetting = React.useMemo(() => {
    if (dataType != NodeType.UseCase) {
      return getDefaultDataAPI(dataType, getAPI(), workspaceSettings);
    }
    return getUseCaseSearchAPI(getAPI(), workspaceSettings);
  }, [dataType, workspaceSettings?.props?.showTSPUseCases, workspaceSettings]);

  const selectedWorkspace = useSelector(s => s.planning.selectedWorkspace);
  const schemas = selectedWorkspace.schemas;
  const schema = useSelector(s => s.view.selected.dataSchema);
  const cellData = data.find(item => item.id === selectedCell.item.id);

  const fieldRequired = (field?: SchemaField) => {
    const schemaForType = getSchema(
      schemas,
      selectedCell.field.schemaInfo.schemaType
    );
    const schemaForms = schemaForType.schemaForms;

    if (selectedCell.field.schemaInfo.schemaType != 'Item') {
      return selectedCell.field.schemaInfo.schemaField.requiredAt ==
        'NotRequired'
        ? {
            isRequired: false,
            schemaField: selectedCell.field.schemaInfo.schemaField
          }
        : {
            isRequired: true,
            schemaField: selectedCell.field.schemaInfo.schemaField
          };
    }

    const schemaForm = schemaForms.find(
      sf => sf.props.itemType == selectedCell?.item?.props?.itemType
    );
    const schemaField = schemaForm?.props.fields.find(f => {
      if (field) {
        // called for each branch in tree field
        return f.key == field.key;
      } else {
        // field is pulled directly from single value selectedCell
        return f.key == selectedCell?.field?.field;
      }
    });
    const isRequired = fieldIsRequired(schemaField, data);
    return { isRequired, schemaField };
  };

  async function saveSelectedTreeCell(deselect: boolean): Promise<boolean> {
    const ts = TreeFieldUtils.getTreeSetting(
      selectedCell?.field?.schemaInfo?.schema,
      parentTreeField?.field
        ? parentTreeField
        : selectedCell?.field?.schemaInfo?.schemaField,
      schemasProp
    );

    const updatedFields: FieldData[] = ts?.settings?.map(s => {
      return { field: s.field, value: editValueTree?.props[s?.field?.field] };
    });
    const cellIdentifier = {
      refId: selectedCell?.item?.props?.refId,
      field: selectedCell?.field?.field
    };

    let hasRequiredEmptyField = false;
    let emptyFieldName = '';

    if (updatedFields && updatedFields.length > 0) {
      for (const field of updatedFields) {
        const { isRequired } = fieldRequired(field.field);
        const isEmpty =
          !field.value ||
          (Array.isArray(field.value) && field.value.length === 0);

        if (isRequired && isEmpty) {
          hasRequiredEmptyField = true;
          emptyFieldName = field.field.name || 'This field';
          break;
        }
      }
    }

    if (hasRequiredEmptyField) {
      setErrorMessage(`${emptyFieldName} is required.`);
      return false;
    }
    setErrorMessage(null);

    removeInvalidCell(cellIdentifier.refId, cellIdentifier.field);

    // Do not save if no changes are made
    if (!updatedFields?.length) {
      return true;
    }

    updateFields(selectedCell?.item, updatedFields);

    if (deselect) {
      setSelectedCell(null);
    }

    try {
      await updateData(selectedCell?.item, updatedFields);
      return true;
    } catch (e) {
      console.error('Error updating tree data:', e);
      return false;
    }
  }

  const saveSelectedCell = async (deselect: boolean): Promise<boolean> => {
    const { isRequired, schemaField } = fieldRequired();

    const isEmpty =
      !editValue ||
      (typeof editValue === 'string' && editValue.trim().length === 0) ||
      (Array.isArray(editValue) && editValue.length === 0);

    const invalidInput =
      field &&
      cellData &&
      isRequired &&
      isEmpty &&
      schemaField?.dataType !== DataType.boolean;

    if (invalidInput) {
      setErrorMessage(`${schemaField?.name || 'This field'} is required.`);
      return false;
    }
    setErrorMessage(null);

    const revertToOriginal = (value: any) => {
      updateField(selectedCell?.item, schemaField, value);
      if (deselect) {
        setSelectedCell(null);
      }
      return;
    };

    const cellIdentifier = {
      refId: selectedCell?.item?.props?.refId,
      field: selectedCell?.field?.field
    };

    removeInvalidCell(cellIdentifier.refId, cellIdentifier.field);
    const dataType = selectedCell?.field?.schemaInfo?.schemaField?.dataType;
    if (areObjectsEqual(editValue, originalValue)) {
      if (deselect) {
        setSelectedCell(null);
      }
      return true;
    }
    if (dataType == DataType.boolean && !editValue && originalValue == null) {
      if (deselect) {
        setSelectedCell(null);
      }
      return true;
    }

    if (dataType == DataType.date || dataType == DataType.dateTime) {
      const formattedValue = formatDate(editValue, true);
      updateField(selectedCell?.item, schemaField, formattedValue);

      if (deselect) {
        setSelectedCell(null);
      }
      await updateData(
        selectedCell?.item,
        schemaField,
        formattedValue,
        toasters
      ).catch(e => {
        revertToOriginal(formatDate(originalValue, true));
        return false;
      });
      return true;
    } else {
      updateField(selectedCell?.item, schemaField, editValue);

      if (deselect) {
        setSelectedCell(null);
      }

      try {
        await updateData(selectedCell?.item, schemaField, editValue, toasters);
        return true;
      } catch (e) {
        console.error('Error updating data:', e);
        revertToOriginal(originalValue);
        return false;
      }
    }
  };

  const handleKeyPress = async event => {
    if (event.key === KeyName.ESCAPE) {
      event.preventDefault();
      setSelectedCell(null);
      return;
    }
    if (event.altKey && event.key === KeyName.ENTER) {
      if (
        selectedCell?.field?.schemaInfo?.schemaField?.dataType ==
        DataType.longString
      ) {
        event.preventDefault();
        setEditValue(editValue + '\n');
      }
      return;
    }

    if (event.key === KeyName.ENTER || event.key === KeyName.TAB) {
      const currentFieldIndex = fields?.findIndex(
        f => f.field == selectedCell?.field?.field
      );
      const currentItemIndex = data?.findIndex(
        i => i.props?.refId == selectedCell?.item?.props?.refId
      );
      // Save and move down a row
      if (event.key === KeyName.ENTER) {
        event.preventDefault(); // Prevent default enter behavior

        const saveResult = await saveSelectedCell(false);
        if (!saveResult) {
          return;
        }

        const isLastItem = currentItemIndex === data.length - 1;

        let newSelectedCell = {
          field: fields[currentFieldIndex],
          item: isLastItem ? data[0] : data[currentItemIndex + 1]
        };

        let fieldIndexInc = 0;
        let itemIndexInc = isLastItem ? 0 : 1;
        let safetyCounter = 0;
        const maxAttempts = Math.min(data.length * fields.length, 1000);

        let nextCell = isCellValid(
          newSelectedCell.item,
          newSelectedCell.field,
          document
        );

        while (!nextCell && safetyCounter < maxAttempts) {
          safetyCounter++;
          itemIndexInc++;
          const nextItemIndex = (currentItemIndex + itemIndexInc) % data.length;
          if (nextItemIndex === currentItemIndex) {
            fieldIndexInc++;
            itemIndexInc = 1;
            if (fieldIndexInc >= fields.length) {
              break;
            }
          }

          const nextFieldIndex =
            (currentFieldIndex + fieldIndexInc) % fields.length;

          newSelectedCell = {
            field: fields[nextFieldIndex],
            item: data[nextItemIndex]
          };
          nextCell = isCellValid(
            newSelectedCell.item,
            newSelectedCell.field,
            document
          );
        }
        if (nextCell) {
          setSelectedCell(newSelectedCell);
        } else {
          console.warn(
            'Could not find a valid cell to navigate to after trying all combinations'
          );
          setSelectedCell(null);
        }
      }
      // Save and move right a column
      if (event.key === KeyName.TAB) {
        event.preventDefault(); // Prevent default tab behavior

        const saveResult = await saveSelectedCell(false);
        if (!saveResult) {
          return;
        }

        // Modified Tab navigation logic to move to the next row when reaching the end of a row
        // and loop back to the first row when reaching the end of the last row
        const isLastField = currentFieldIndex === fields.length - 1;
        const isLastItem = currentItemIndex === data.length - 1;

        let newSelectedCell = {
          field: isLastField ? fields[0] : fields[currentFieldIndex + 1],
          item: isLastField
            ? isLastItem
              ? data[0]
              : data[currentItemIndex + 1]
            : data[currentItemIndex]
        };

        let fieldIndexInc = isLastField ? 0 : 1;
        let itemIndexInc = isLastField ? (isLastItem ? 0 : 1) : 0;
        let safetyCounter = 0;
        const maxAttempts = Math.min(data.length * fields.length, 1000);

        let nextCell = isCellValid(
          newSelectedCell.item,
          newSelectedCell.field,
          document
        );
        while (!nextCell && safetyCounter < maxAttempts) {
          safetyCounter++;

          let nextFieldIndex =
            (currentFieldIndex + fieldIndexInc) % fields.length;

          if (nextFieldIndex === 0 && fieldIndexInc > 0) {
            itemIndexInc++;
          }

          let nextItemIndex = (currentItemIndex + itemIndexInc) % data.length;

          newSelectedCell = {
            field: fields[nextFieldIndex],
            item: data[nextItemIndex]
          };

          nextCell = isCellValid(
            newSelectedCell.item,
            newSelectedCell.field,
            document
          );

          if (!nextCell) {
            fieldIndexInc++;
            if (fieldIndexInc > fields.length) {
              fieldIndexInc = 0;
              itemIndexInc++;
            }
          }
        }
        if (nextCell) {
          setSelectedCell(newSelectedCell);
        } else {
          console.warn(
            'Could not find a valid cell to navigate to after trying all combinations'
          );
          setSelectedCell(null);
        }
      }
      updateSelectedCellPosition(document, setSelectedCellPosition);
    }
  };

  const scrollYRef = React.useRef(0);
  const scrollXRef = React.useRef(0);
  const hasLockedScrollRef = React.useRef(false);
  const tabularScrollPositions = React.useRef<
    Map<HTMLElement, { x: number; y: number }>
  >(new Map());

  const lockScrolling = React.useCallback(() => {
    if (hasLockedScrollRef.current) return;

    const scrollY = window.scrollY || document.documentElement.scrollTop;
    const scrollX = window.scrollX || document.documentElement.scrollLeft;
    scrollYRef.current = scrollY;
    scrollXRef.current = scrollX;

    document.body.classList.add('edit-mode-active');

    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = `-${scrollX}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';

    // Find all scrollable containers related to the tabular view
    const scrollableContainers = [
      ...document.querySelectorAll('.tabular'),
      ...document.querySelectorAll('.tabular-container'),
      ...document.querySelectorAll('.tabular-view'),
      ...document.querySelectorAll('.tabular-header-row'),
      ...document.querySelectorAll('.scrollable-container')
    ];

    tabularScrollPositions.current.clear();

    scrollableContainers.forEach(container => {
      const element = container as HTMLElement;

      // Skip if already processed (might be selected by multiple queries)
      if (element.dataset.scrollLocked === 'true') return;

      // Store original overflow and scroll positions
      element.dataset.originalOverflow = element.style.overflow;
      element.dataset.scrollLocked = 'true';

      // Store scroll position for this element
      tabularScrollPositions.current.set(element, {
        x: element.scrollLeft,
        y: element.scrollTop
      });
      element.style.overflow = 'hidden';
    });

    hasLockedScrollRef.current = true;
  }, []);

  const restoreScrolling = React.useCallback(() => {
    if (!hasLockedScrollRef.current) return;

    document.body.classList.remove('edit-mode-active');

    const lockedElements = document.querySelectorAll(
      '[data-scroll-locked="true"]'
    );

    lockedElements.forEach(container => {
      const element = container as HTMLElement;

      // Restore original overflow style
      if (element.dataset.originalOverflow) {
        element.style.overflow = element.dataset.originalOverflow;
        delete element.dataset.originalOverflow;
      } else {
        element.style.overflow = '';
      }

      // Remove our marker attribute
      delete element.dataset.scrollLocked;

      // Restore scroll position if we saved it
      const savedPosition = tabularScrollPositions.current.get(element);
      if (savedPosition) {
        // Use setTimeout to ensure the overflow property has been applied
        setTimeout(() => {
          element.scrollLeft = savedPosition.x;
          element.scrollTop = savedPosition.y;
        }, 0);
      }
    });

    // Restore body styling
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.width = '';
    document.body.style.overflow = '';

    // Restore window scroll position
    requestAnimationFrame(() => {
      window.scrollTo(scrollXRef.current, scrollYRef.current);
      hasLockedScrollRef.current = false;
    });

    // Clear the stored positions
    tabularScrollPositions.current.clear();
  }, []);

  React.useEffect(() => {
    const keyPressHandler = async (event: KeyboardEvent) =>
      await handleKeyPress(event);

    document.addEventListener('keydown', keyPressHandler);
    setTimeout(lockScrolling, 100);
    return () => {
      document.removeEventListener('keydown', keyPressHandler);
      restoreScrolling();
    };
  }, [selectedCell, editValue, lockScrolling, restoreScrolling]);

  const ts = React.useMemo(
    () =>
      TreeFieldUtils.getTreeSetting(
        selectedCell?.field?.schemaInfo?.schema,
        parentTreeField
          ? parentTreeField
          : selectedCell?.field?.schemaInfo?.schemaField,
        schemas
      ),
    [dataType, schemas, parentTreeField]
  );
  React.useEffect(() => {
    setIsTreeSelect(!!ts);
  }, [ts]);

  React.useEffect(() => {
    setDataType(null);
    setFieldType(null);
    setParentTreeField(null);
    setErrorMessage(null); // Clear error message when cell changes
    const treeFieldMatches = treeFieldGroupings?.filter(
      f =>
        f.childField?.field ==
        selectedCell?.field?.schemaInfo?.schemaField.field
    );
    if (treeFieldMatches?.length) {
      setParentTreeField(treeFieldMatches[0].parentField);
    }
    updateEditValue(selectedCell, setEditValue, setOriginalValue);
    setRefreshSearchTrigger(true);
  }, [selectedCell]);
  React.useEffect(() => {
    setBoxAdjustments(
      generateBoxAdjustments(
        selectedCellPosition,
        minEditBoxWidth,
        selectedCell?.field?.schemaInfo?.schemaField,
        !!ts,
        totalColumnCount
      )
    );
  }, [selectedCellPosition, editRef.current]);

  React.useEffect(() => {
    if (fieldType == null) {
      setFieldType(selectedCell?.field?.schemaInfo?.schemaField?.fieldType);
    }
    if (dataType == null) {
      setDataType(selectedCell?.field?.schemaInfo?.schemaField?.dataType);
    }
  }, [fieldType, dataType]);

  const getTextEditDisplay = () => {
    const editRefPosition = editRef?.current?.getBoundingClientRect();
    return (
      <div className="edit-cell-wrapper">
        <TextareaAutosize
          maxRows="8"
          minRows="1"
          value={editValue}
          onChange={(e: any) => {
            // If the dataType is a number, prevent non-numeric characters
            if (dataType == DataType.number) {
              setEditValue(e.target.value.replace(/\D/g, ''));
            } else {
              setEditValue(e.target.value);
            }
            // Clear error when user types
            if (errorMessage) setErrorMessage(null);
          }}
          className={`edit-mode-textarea ${errorMessage ? 'error' : ''}`}
          style={{
            maxHeight: Math.abs(
              window?.innerHeight - editRefPosition?.top - 22.5
            ),
            maxWidth: Math.abs(window?.innerWidth - editRefPosition?.left - 6),
            minWidth: Math.abs(selectedCellPosition?.width),
            resize: 'both',
            pointerEvents: 'all'
          }}
          ref={textareaRef}
        />
        {errorMessage && <div className="edit-cell-error">{errorMessage}</div>}
        <span
          className="edit-cell-buttons"
          style={{ pointerEvents: 'none', maxWidth: 105, paddingTop: '4px' }}
        >
          <div style={{ pointerEvents: 'all', display: 'flex', gap: '4px' }}>
            <Cancel buttonSize="small" onClick={() => setSelectedCell(null)} />{' '}
            <Save
              buttonSize="small"
              disabled={
                (ts
                  ? !ts.settings?.some(
                      s => editValueTree?.props[s?.field?.field]
                    )
                  : false) || areObjectsEqual(editValue, originalValue)
              }
              onClick={async () => await saveSelectedCell(true)}
            />
          </div>
        </span>
      </div>
    );
  };

  const getBooleanEditDisplay = () => {
    const editRefPosition = editRef?.current?.getBoundingClientRect();
    return (
      <div className="edit-cell-wrapper">
        <div className="filter-box boolean-filter">
          <div
            className={`edit-mode-textarea edit-cell-checkbox-area ${
              errorMessage ? 'error' : ''
            }`}
            style={{
              maxHeight: 27.5,
              minHeight: 27.5,
              maxWidth: selectedCellPosition?.width,
              minWidth: Math.abs(selectedCellPosition?.width),
              resize: 'both',
              pointerEvents: 'all'
            }}
            ref={textareaRef}
          >
            <div className="edit-cell-checkbox">
              <input
                type="checkbox"
                onChange={o => {
                  o?.target?.checked == true
                    ? setEditValue(true)
                    : setEditValue(false);

                  // Clear error when user checks/unchecks
                  if (errorMessage) setErrorMessage(null);
                }}
                checked={editValue === true ? true : false}
              ></input>
            </div>
          </div>
        </div>
        {errorMessage && <div className="edit-cell-error">{errorMessage}</div>}
        <span
          className="edit-cell-buttons"
          style={{ pointerEvents: 'none', maxWidth: 105, paddingTop: '4px' }}
        >
          {' '}
          <div style={{ pointerEvents: 'all', display: 'flex', gap: '4px' }}>
            <Cancel buttonSize="small" onClick={() => setSelectedCell(null)} />
            <Save
              buttonSize="small"
              disabled={
                areObjectsEqual(editValue, originalValue) ||
                (originalValue == null && editValue == false)
              }
              onClick={async () => await saveSelectedCell(true)}
            />
          </div>
        </span>
      </div>
    );
  };

  const getDateEditDisplay = () => {
    const editRefPosition = editRef?.current?.getBoundingClientRect();
    return (
      <div className="edit-cell-wrapper">
        <div
          style={{
            maxHeight: Math.abs(
              window?.innerHeight - editRefPosition?.top - 22.5
            ),
            maxWidth: Math.abs(window?.innerWidth - editRefPosition?.left - 6),
            minWidth: Math.abs(selectedCellPosition?.width),
            resize: 'both',
            pointerEvents: 'all'
          }}
        >
          <DatePicker
            selected={editValue}
            onChange={date => {
              setEditValue(date);
              // Clear error when user selects a date
              if (errorMessage) setErrorMessage(null);
            }}
            startOpen={true}
            disabledKeyboardNavigation={true}
            isClearable={true}
            className={`edit-mode-textarea ${errorMessage ? 'error' : ''}`}
          />
          {errorMessage && (
            <div className="edit-cell-error">{errorMessage}</div>
          )}
          <span
            className="edit-cell-buttons"
            style={{ pointerEvents: 'none', maxWidth: 105, paddingTop: '4px' }}
          >
            <div style={{ pointerEvents: 'all', display: 'flex', gap: '4px' }}>
              <Cancel
                buttonSize="small"
                onClick={() => setSelectedCell(null)}
              />
              <Save
                buttonSize="small"
                disabled={areObjectsEqual(editValue, originalValue)}
                onClick={async () => await saveSelectedCell(true)}
              />
            </div>
          </span>
        </div>
      </div>
    );
  };

  const getReferenceEditDisplay = () => {
    const editRefPosition = editRef?.current?.getBoundingClientRect();
    return (
      <div className="edit-cell-wrapper flex-column">
        <div
          className={`edit-cell-content ${errorMessage ? 'error' : ''}`}
          style={{
            maxHeight: Math.abs(
              window?.innerHeight - editRefPosition?.top - 22.5
            ),
            maxWidth: Math.abs(window?.innerWidth - editRefPosition?.left - 6),
            minWidth: Math.abs(selectedCellPosition?.width)
          }}
        >
          {!!ts && (
            <Field
              key={selectedCell?.field?.field}
              field={
                parentTreeField
                  ? parentTreeField
                  : selectedCell?.field?.schemaInfo?.schemaField
              }
              data={editValueTree}
              schema={schema}
              schemas={schemas}
              updater={updater}
              isEditMode={true}
            />
          )}
          {!ts && (
            <ReferenceSearch<Metadata>
              inlineMenu={true}
              api={refSearchSetting?.api}
              seeOptions={refSearchSetting?.seeOptions}
              minLength={0}
              maxResults={50}
              open={true}
              cache={cache}
              multiple={
                selectedCell?.field?.schemaInfo?.schemaField?.multiValue
              }
              includeInactives={false}
              includeBlanks={false}
              nodeType={dataType as NodeType}
              selected={editValue}
              hideIcon={true}
              onChange={values => {
                setEditValue(values);
                // Clear error when user selects a reference
                if (errorMessage) setErrorMessage(null);
              }}
              refreshSearchTrigger={refreshSearchTrigger}
              setRefreshSearchTrigger={setRefreshSearchTrigger}
            />
          )}
        </div>
        {errorMessage && <div className="edit-cell-error">{errorMessage}</div>}
        <div className="edit-cell-buttons-container">
          <div className="edit-cell-buttons-inner">
            <Cancel buttonSize="small" onClick={() => setSelectedCell(null)} />
            <Save
              buttonSize="small"
              disabled={
                !!ts
                  ? areObjectsEqual(
                      editValueTree?.props,
                      originalValueTree?.props
                    )
                  : areObjectsEqual(editValue, originalValue)
              }
              onClick={async () => {
                if (!!ts) {
                  await saveSelectedTreeCell(true);
                } else {
                  await saveSelectedCell(true);
                }
              }}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      className="edit-cell-container"
      ref={editRef}
      id="editCellContainer"
      onClick={e => e.stopPropagation()}
      onMouseEnter={e => e.stopPropagation()}
      onMouseLeave={e => e.stopPropagation()}
      style={{
        top:
          selectedCellPosition?.top - 55 - boxAdjustments?.editBottomAdjustment,
        left:
          selectedCellPosition?.left -
          navFlyoutWidth -
          boxAdjustments?.editRightAdjustment,
        minWidth: minEditBoxWidth,
        maxHeight: Math.abs(
          window?.innerHeight - selectedCellPosition?.top - 22.5
        ),
        maxWidth: Math.abs(window?.innerWidth - selectedCellPosition?.left - 7),
        pointerEvents: 'all'
      }}
    >
      {(dataType == DataType.string ||
        dataType == DataType.longString ||
        dataType == DataType.number) &&
        getTextEditDisplay()}
      {dataType == DataType.boolean && getBooleanEditDisplay()}
      {(dataType == DataType.date || dataType == DataType.dateTime) &&
        getDateEditDisplay()}
      {fieldType == FieldType.reference && getReferenceEditDisplay()}
    </div>
  );
};
